import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Save, ArrowLeft, Plus, Trash2, Image, Type, Quote, List, Code, AlertCircle, Eye, Clock, CheckCircle, Upload, Film, Music } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import MediaUploader from '../../components/MediaUploader';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const blockTypes = [
  { type: 'heading', label: 'Heading', icon: Type },
  { type: 'paragraph', label: 'Paragraph', icon: Type },
  { type: 'quote', label: 'Quote', icon: Quote },
  { type: 'image', label: 'Image', icon: Image },
  { type: 'callout', label: 'Callout', icon: AlertCircle },
  { type: 'list', label: 'List', icon: List },
  { type: 'embed', label: 'Embed', icon: Code },
  { type: 'video', label: 'Video', icon: Film },
  { type: 'audio', label: 'Audio', icon: Music },
];

const BlockEditor = ({ block, onChange, onRemove }) => {
  const updateContent = (key, value) => onChange({ ...block, content: { ...block.content, [key]: value } });

  return (
    <div className="bg-navy border border-gold/[0.06] rounded-lg p-4 group relative" data-testid={`block-${block.id}`}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-[10px] uppercase tracking-wider text-cream/20">{block.type}</span>
        <button onClick={onRemove} className="opacity-0 group-hover:opacity-100 p-1 text-cream/20 hover:text-red-400 transition-all"><Trash2 size={12} /></button>
      </div>
      {block.type === 'heading' && (
        <div className="space-y-2">
          <select value={block.content.level || 'h2'} onChange={(e) => updateContent('level', e.target.value)} className="bg-navy-deep border border-cream/10 rounded px-2 py-1 text-xs text-cream/50">
            <option value="h2">H2</option><option value="h3">H3</option><option value="h4">H4</option>
          </select>
          <input value={block.content.text || ''} onChange={(e) => updateContent('text', e.target.value)} placeholder="Heading text..." className="w-full bg-transparent text-cream text-lg font-serif border-none outline-none placeholder-cream/20" />
        </div>
      )}
      {block.type === 'paragraph' && (
        <textarea value={block.content.text || ''} onChange={(e) => updateContent('text', e.target.value)} placeholder="Write your content..." rows={4} className="w-full bg-transparent text-cream/70 text-sm leading-relaxed border-none outline-none resize-none placeholder-cream/20" />
      )}
      {block.type === 'quote' && (
        <div className="border-l-2 border-gold/30 pl-4 space-y-2">
          <textarea value={block.content.text || ''} onChange={(e) => updateContent('text', e.target.value)} placeholder="Quote text..." rows={2} className="w-full bg-transparent text-cream/60 font-serif italic border-none outline-none resize-none placeholder-cream/20" />
          <input value={block.content.attribution || ''} onChange={(e) => updateContent('attribution', e.target.value)} placeholder="Attribution..." className="w-full bg-transparent text-cream/30 text-xs border-none outline-none placeholder-cream/15" />
        </div>
      )}
      {block.type === 'image' && (
        <div className="space-y-2">
          <MediaUploader
            accept="image"
            folder="articles"
            label="Upload article image"
            onUpload={(url) => updateContent('url', url)}
          />
          <input value={block.content.url || ''} onChange={(e) => updateContent('url', e.target.value)} placeholder="Or paste image URL..." className="w-full bg-navy-deep border border-cream/10 rounded px-3 py-2 text-sm text-cream placeholder-cream/20" data-testid="image-url-input" />
          {block.content.url && (
            <img src={block.content.url} alt="Preview" className="w-full max-h-40 object-cover rounded border border-cream/[0.06]" />
          )}
          <input value={block.content.caption || ''} onChange={(e) => updateContent('caption', e.target.value)} placeholder="Caption..." className="w-full bg-navy-deep border border-cream/10 rounded px-3 py-2 text-xs text-cream/50 placeholder-cream/15" />
          <input value={block.content.alt || ''} onChange={(e) => updateContent('alt', e.target.value)} placeholder="Alt text..." className="w-full bg-navy-deep border border-cream/10 rounded px-3 py-2 text-xs text-cream/50 placeholder-cream/15" />
        </div>
      )}
      {block.type === 'video' && (
        <div className="space-y-2">
          <MediaUploader
            accept="video"
            folder="articles"
            label="Upload video"
            onUpload={(url) => updateContent('url', url)}
          />
          <input value={block.content.url || ''} onChange={(e) => updateContent('url', e.target.value)} placeholder="Or paste video URL (YouTube/Vimeo/direct)..." className="w-full bg-navy-deep border border-cream/10 rounded px-3 py-2 text-sm text-cream placeholder-cream/20" data-testid="video-url-input" />
          {block.content.url && block.content.url.includes('/api/media/') && (
            <video src={block.content.url} controls className="w-full max-h-40 rounded border border-cream/[0.06]" />
          )}
          <input value={block.content.caption || ''} onChange={(e) => updateContent('caption', e.target.value)} placeholder="Caption..." className="w-full bg-navy-deep border border-cream/10 rounded px-3 py-2 text-xs text-cream/50 placeholder-cream/15" />
        </div>
      )}
      {block.type === 'audio' && (
        <div className="space-y-2">
          <MediaUploader
            accept="audio"
            folder="articles"
            label="Upload audio clip"
            onUpload={(url) => updateContent('url', url)}
          />
          <input value={block.content.url || ''} onChange={(e) => updateContent('url', e.target.value)} placeholder="Or paste audio URL..." className="w-full bg-navy-deep border border-cream/10 rounded px-3 py-2 text-sm text-cream placeholder-cream/20" data-testid="audio-url-input" />
          {block.content.url && (
            <audio src={block.content.url} controls className="w-full mt-1" />
          )}
          <input value={block.content.caption || ''} onChange={(e) => updateContent('caption', e.target.value)} placeholder="Caption..." className="w-full bg-navy-deep border border-cream/10 rounded px-3 py-2 text-xs text-cream/50 placeholder-cream/15" />
        </div>
      )}
      {block.type === 'callout' && (
        <div className="bg-gold/[0.04] border border-gold/10 rounded-lg p-3">
          <textarea value={block.content.text || ''} onChange={(e) => updateContent('text', e.target.value)} placeholder="Callout text..." rows={2} className="w-full bg-transparent text-gold/70 text-sm border-none outline-none resize-none placeholder-gold/30" />
        </div>
      )}
      {block.type === 'list' && (
        <textarea value={block.content.text || ''} onChange={(e) => updateContent('text', e.target.value)} placeholder="One item per line..." rows={4} className="w-full bg-transparent text-cream/60 text-sm border-none outline-none resize-none placeholder-cream/20" />
      )}
      {block.type === 'embed' && (
        <input value={block.content.url || ''} onChange={(e) => updateContent('url', e.target.value)} placeholder="YouTube/Vimeo URL or embed code..." className="w-full bg-navy-deep border border-cream/10 rounded px-3 py-2 text-sm text-cream placeholder-cream/20" />
      )}
    </div>
  );
};

const AdminArticleEditor = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = !id || id === 'new';
  const { token } = useAuth();
  
  const [article, setArticle] = useState({
    title: '', slug: '', excerpt: '', content_blocks: [], categories: [], tags: [],
    author: 'Dr Promise Monday', featured_image: '', og_image: '', meta_title: '',
    meta_description: '', canonical_url: '', status: 'draft', publish_at: '',
    unpublish_at: '', featured: false, pinned: false, notify_subscribers: false,
  });
  const [saving, setSaving] = useState(false);
  const [showSeo, setShowSeo] = useState(false);
  const [tagInput, setTagInput] = useState('');

  useEffect(() => {
    if (!isNew) {
      const fetchArticle = async () => {
        const res = await fetch(`${API_URL}/api/cms/articles/${id}`, { headers: { Authorization: `Bearer ${token}` } });
        if (res.ok) {
          const data = await res.json();
          setArticle(data);
          setTagInput((data.tags || []).join(', '));
        }
      };
      fetchArticle();
    }
  }, [id, isNew]);

  const addBlock = (type) => {
    const newBlock = { id: crypto.randomUUID(), type, content: {} };
    setArticle({ ...article, content_blocks: [...article.content_blocks, newBlock] });
  };

  const updateBlock = (index, block) => {
    const blocks = [...article.content_blocks];
    blocks[index] = block;
    setArticle({ ...article, content_blocks: blocks });
  };

  const removeBlock = (index) => {
    const blocks = article.content_blocks.filter((_, i) => i !== index);
    setArticle({ ...article, content_blocks: blocks });
  };

  const handleSave = async (publishStatus) => {
    setSaving(true);
    const payload = { ...article, tags: tagInput.split(',').map(t => t.trim()).filter(Boolean) };
    if (publishStatus) payload.status = publishStatus;

    const method = isNew ? 'POST' : 'PUT';
    const url = isNew ? `${API_URL}/api/cms/articles` : `${API_URL}/api/cms/articles/${id}`;
    
    try {
      const res = await fetch(url, { method, headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify(payload) });
      if (res.ok) {
        const saved = await res.json();
        if (isNew) navigate(`/admin/articles/${saved.id}/edit`, { replace: true });
        else setArticle(saved);
      }
    } catch (e) { console.error(e); }
    setSaving(false);
  };

  return (
    <div data-testid="article-editor">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/admin/articles')} className="p-2 text-cream/30 hover:text-gold transition-colors rounded-lg"><ArrowLeft size={18} /></button>
          <h1 className="font-serif text-xl text-cream font-semibold">{isNew ? 'New Article' : 'Edit Article'}</h1>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => handleSave('draft')} disabled={saving} className="px-3 py-2 border border-cream/10 text-cream/50 text-xs rounded-lg hover:border-gold/20 hover:text-gold transition-all disabled:opacity-50" data-testid="save-draft-btn">
            <Save size={12} className="inline mr-1" /> Save Draft
          </button>
          <button onClick={() => handleSave('published')} disabled={saving} className="px-4 py-2 bg-gold text-navy text-xs font-semibold rounded-lg hover:bg-gold-light transition-all disabled:opacity-50" data-testid="publish-btn">
            <CheckCircle size={12} className="inline mr-1" /> Publish
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Main content */}
        <div className="lg:col-span-3 space-y-4">
          <input value={article.title} onChange={(e) => setArticle({ ...article, title: e.target.value })} placeholder="Article title..." className="w-full bg-transparent text-cream text-2xl font-serif font-semibold border-none outline-none placeholder-cream/15" data-testid="article-title" />
          <input value={article.excerpt} onChange={(e) => setArticle({ ...article, excerpt: e.target.value })} placeholder="Short excerpt..." className="w-full bg-transparent text-cream/40 text-sm border-none outline-none placeholder-cream/15" data-testid="article-excerpt" />

          {/* Blocks */}
          <div className="space-y-3">
            {article.content_blocks.map((block, i) => (
              <BlockEditor key={block.id} block={block} onChange={(b) => updateBlock(i, b)} onRemove={() => removeBlock(i)} />
            ))}
          </div>

          {/* Add block */}
          <div className="flex flex-wrap gap-1 p-3 bg-navy border border-dashed border-cream/[0.06] rounded-lg">
            {blockTypes.map((bt) => (
              <button key={bt.type} onClick={() => addBlock(bt.type)} className="flex items-center gap-1.5 px-3 py-1.5 text-cream/30 hover:text-gold hover:bg-gold/[0.05] rounded text-xs transition-all" data-testid={`add-${bt.type}-block`}>
                <bt.icon size={12} /> {bt.label}
              </button>
            ))}
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-4">
          {/* Status & Schedule */}
          <div className="bg-navy border border-gold/[0.06] rounded-xl p-4">
            <p className="text-[10px] uppercase tracking-wider text-cream/20 mb-2">Status</p>
            <select value={article.status} onChange={(e) => {
              const newStatus = e.target.value;
              const updates = { status: newStatus };
              if (newStatus === 'published' && !article.publish_at) {
                updates.publish_at = new Date().toISOString();
              }
              setArticle({ ...article, ...updates });
            }} className="w-full bg-navy-deep border border-cream/10 rounded-lg px-3 py-2 text-sm text-cream" style={{colorScheme:'dark'}} data-testid="article-status-select">
              <option value="draft">Draft</option>
              <option value="scheduled">Scheduled</option>
              <option value="published">Published</option>
              <option value="archived">Archived</option>
            </select>

            <div className="mt-3">
              <p className="text-[10px] uppercase tracking-wider text-cream/20 mb-1.5">Publish Date</p>
              <input
                type="datetime-local"
                value={article.publish_at ? article.publish_at.slice(0, 16) : ''}
                onChange={(e) => setArticle({ ...article, publish_at: e.target.value ? new Date(e.target.value).toISOString() : '' })}
                className="w-full bg-navy-deep border border-cream/10 rounded-lg px-3 py-2 text-xs text-cream"
                style={{colorScheme:'dark'}}
                data-testid="publish-date-input"
              />
              <p className="text-cream/10 text-[9px] mt-1">
                {article.status === 'published' && article.publish_at && new Date(article.publish_at) > new Date()
                  ? 'Scheduled: will appear on public feed at this date'
                  : article.status === 'scheduled'
                  ? 'Will auto-publish at this date'
                  : 'When this article was/will be published'}
              </p>
            </div>

            {article.publish_at && (
              <div className="mt-2">
                <p className="text-[10px] uppercase tracking-wider text-cream/20 mb-1.5">Unpublish Date <span className="text-cream/10">(optional)</span></p>
                <input
                  type="datetime-local"
                  value={article.unpublish_at ? article.unpublish_at.slice(0, 16) : ''}
                  onChange={(e) => setArticle({ ...article, unpublish_at: e.target.value ? new Date(e.target.value).toISOString() : '' })}
                  className="w-full bg-navy-deep border border-cream/10 rounded-lg px-3 py-2 text-xs text-cream"
                  style={{colorScheme:'dark'}}
                  data-testid="unpublish-date-input"
                />
              </div>
            )}
          </div>

          {/* Author */}
          <div className="bg-navy border border-gold/[0.06] rounded-xl p-4">
            <p className="text-[10px] uppercase tracking-wider text-cream/20 mb-2">Author</p>
            <input value={article.author} onChange={(e) => setArticle({ ...article, author: e.target.value })} className="w-full bg-navy-deep border border-cream/10 rounded-lg px-3 py-2 text-sm text-cream" />
          </div>

          {/* Tags */}
          <div className="bg-navy border border-gold/[0.06] rounded-xl p-4">
            <p className="text-[10px] uppercase tracking-wider text-cream/20 mb-2">Tags</p>
            <input value={tagInput} onChange={(e) => setTagInput(e.target.value)} placeholder="tag1, tag2, tag3" className="w-full bg-navy-deep border border-cream/10 rounded-lg px-3 py-2 text-sm text-cream placeholder-cream/15" />
          </div>

          {/* Featured Image — with upload */}
          <div className="bg-navy border border-gold/[0.06] rounded-xl p-4">
            <p className="text-[10px] uppercase tracking-wider text-cream/20 mb-2">Featured Image</p>
            <MediaUploader
              accept="image"
              folder="articles"
              label="Upload featured image"
              onUpload={(url) => setArticle(prev => ({ ...prev, featured_image: url }))}
            />
            <input value={article.featured_image || ''} onChange={(e) => setArticle({ ...article, featured_image: e.target.value })} placeholder="Or paste URL..." className="w-full mt-2 bg-navy-deep border border-cream/10 rounded-lg px-3 py-2 text-xs text-cream placeholder-cream/15" data-testid="featured-image-input" />
            {article.featured_image && (
              <img src={article.featured_image} alt="Featured" className="w-full h-24 object-cover rounded mt-2 border border-cream/[0.06]" />
            )}
          </div>

          {/* Options */}
          <div className="bg-navy border border-gold/[0.06] rounded-xl p-4 space-y-3">
            <p className="text-[10px] uppercase tracking-wider text-cream/20 mb-1">Options</p>
            {[
              { key: 'featured', label: 'Featured on homepage' },
              { key: 'pinned', label: 'Pin to top' },
              { key: 'notify_subscribers', label: 'Notify subscribers' },
            ].map((opt) => (
              <label key={opt.key} className="flex items-center gap-2 text-cream/40 text-xs cursor-pointer">
                <input type="checkbox" checked={article[opt.key]} onChange={(e) => setArticle({ ...article, [opt.key]: e.target.checked })} className="rounded border-cream/20 bg-navy-deep text-gold focus:ring-gold/30" />
                {opt.label}
              </label>
            ))}
          </div>

          {/* SEO */}
          <button onClick={() => setShowSeo(!showSeo)} className="w-full text-left bg-navy border border-gold/[0.06] rounded-xl p-4 text-cream/30 text-xs hover:text-gold transition-colors">
            {showSeo ? 'Hide' : 'Show'} SEO Fields
          </button>
          {showSeo && (
            <div className="bg-navy border border-gold/[0.06] rounded-xl p-4 space-y-3">
              <div><p className="text-[10px] text-cream/20 mb-1">Slug</p><input value={article.slug} onChange={(e) => setArticle({ ...article, slug: e.target.value })} className="w-full bg-navy-deep border border-cream/10 rounded px-3 py-1.5 text-xs text-cream" /></div>
              <div><p className="text-[10px] text-cream/20 mb-1">Meta Title</p><input value={article.meta_title || ''} onChange={(e) => setArticle({ ...article, meta_title: e.target.value })} className="w-full bg-navy-deep border border-cream/10 rounded px-3 py-1.5 text-xs text-cream" /></div>
              <div><p className="text-[10px] text-cream/20 mb-1">Meta Description</p><textarea value={article.meta_description || ''} onChange={(e) => setArticle({ ...article, meta_description: e.target.value })} rows={2} className="w-full bg-navy-deep border border-cream/10 rounded px-3 py-1.5 text-xs text-cream resize-none" /></div>
              <div><p className="text-[10px] text-cream/20 mb-1">OG Image</p><input value={article.og_image || ''} onChange={(e) => setArticle({ ...article, og_image: e.target.value })} className="w-full bg-navy-deep border border-cream/10 rounded px-3 py-1.5 text-xs text-cream" /></div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminArticleEditor;
